import { sqliteTable, text, integer, real, index, uniqueIndex } from "drizzle-orm/sqlite-core";
import { accounts } from "./accounts";
import { taxTemplates } from "./tax-templates";

// ============================================================================
// items — สินค้า/บริการ (ใช้ใน invoice + POS)
//
// Epic #444 (POS prerequisite #3) added barcode + sku + is_pos_sellable so
// the upcoming POS module can:
//   - Look an item up by EAN-13 / Code-128 scan via
//     `GET /accounting/items/lookup?barcode=X` (or `?sku=Y`).
//   - Filter the cashier search to "POS-sellable" items only (drop services
//     that aren't physically saleable at the till).
//
// Uniqueness: `barcode` and `sku` are unique PER COMPANY, not globally. Two
// tenants may legitimately share an EAN-13 (the manufacturer prints it once
// for the SKU; both tenants stocking that SKU will scan the same number).
// Enforced through `items_company_barcode_idx` / `items_company_sku_idx`
// unique indexes. NULL is allowed — services typically have neither.
// ============================================================================
export const items = sqliteTable("accounting_items", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  itemCode: text("item_code").notNull(),
  itemName: text("item_name").notNull(),
  itemType: text("item_type", {
    enum: ["service", "product"],
  }).notNull(),
  // POS — barcode (EAN-13 / Code-128 / QR payload). Unique per company,
  // NULL allowed, indexed for O(log n) scanner lookup. The companion
  // composite unique index lives in (table) below.
  barcode: text("barcode"),
  // POS — internal SKU. Distinct from `itemCode` (which is the accounting
  // code, may be a chart-of-accounts-style alphanumeric like SRV-001) —
  // SKU is the warehouse/POS code, often shorter and printed on shelf
  // labels. Unique per company, NULL allowed.
  sku: text("sku"),
  // POS — whether the item appears in cashier search. Defaults to 1 so
  // existing items remain visible. Services that are billed-only (e.g.
  // "ค่าเช่ารถ") can flip to 0 to hide from the till without deactivating.
  isPosSellable: integer("is_pos_sellable").notNull().default(1),
  incomeAccountId: text("income_account_id").references(() => accounts.id), // FK → accounts
  expenseAccountId: text("expense_account_id").references(() => accounts.id), // FK → accounts
  defaultRate: real("default_rate"),
  // Epic #443 — Moving Average valuation. Updated by
  // `inventory/services/valuation.service.ts → updateMovingAverage` on every
  // incoming stock movement (purchase, transfer_in, sales-invoice cancel
  // return-leg). Read by sales-invoice deduction + COGS posting via
  // `inventory/services/public-api/valuation.ts`.
  //
  // Multi-warehouse note: per-company average (NOT per-warehouse). Most
  // SMB tenants run a single price for an item across warehouses; switching
  // to per-warehouse is a follow-up if/when fleet/POS needs it. The ledger
  // already keeps `avg_rate` per (item, warehouse) for reporting parity.
  //
  // Backfill: migration 0028 sets it to `defaultRate` for legacy rows so
  // sales of pre-existing items still produce a valuation_amount. Tenants
  // can recompute via a one-off script once they trust the new flow.
  averageCost: real("average_cost"),
  taxTemplateId: text("tax_template_id").references(() => taxTemplates.id), // FK → tax_templates
  unitOfMeasure: text("unit_of_measure"),  // default unit for this item
  isActive: integer("is_active").notNull().default(1),
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  companyBarcodeUnique: uniqueIndex("items_company_barcode_idx").on(table.companyId, table.barcode),
  companySkuUnique: uniqueIndex("items_company_sku_idx").on(table.companyId, table.sku),
  companyPosIdx: index("items_company_pos_idx").on(table.companyId, table.isPosSellable, table.isActive),
}));

// Type exports
export type Item = typeof items.$inferSelect;
export type NewItem = typeof items.$inferInsert;
